/**
 * referralCapture.ts
 * ──────────────────────────────────────────────────────────────────────────────
 * Captures ?ref= query param from any page URL and stores it in sessionStorage
 * so the signup flow can attribute it to the correct referrer.
 *
 * The referral is valid for 6 hours (TTL enforced on read).
 * Call captureReferralFromURL() on every event/hackathon/quiz detail page mount.
 * Call getPendingReferralCode()  inside the signup handler before posting to API.
 */

const REFERRAL_KEY   = 'pendingReferralCode';
const REFERRAL_TIME  = 'pendingReferralTime';
const TTL_MS         = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Read the ?ref= param from the current URL and persist it (with timestamp).
 * Overwrites any previous code only if a new one is found in the URL.
 */
export function captureReferralFromURL(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref    = params.get('ref');

    if (ref && ref.trim()) {
      const code = ref.trim().toUpperCase();
      sessionStorage.setItem(REFERRAL_KEY, code);
      sessionStorage.setItem(REFERRAL_TIME, String(Date.now()));
      console.log('[Referral] Captured ref code:', code);
      return code;
    }
  } catch (e) {
    console.warn('[Referral] captureReferralFromURL error:', e);
  }
  return null;
}

/**
 * Retrieve the stored referral code if it's still within the 6-hour window.
 * Returns null if expired or not set.
 */
export function getPendingReferralCode(): string | null {
  try {
    const code      = sessionStorage.getItem(REFERRAL_KEY);
    const timestamp = sessionStorage.getItem(REFERRAL_TIME);

    if (!code || !timestamp) return null;

    const age = Date.now() - parseInt(timestamp, 10);
    if (age > TTL_MS) {
      clearReferralCode();
      console.log('[Referral] Code expired (>6h), cleared.');
      return null;
    }

    return code;
  } catch (e) {
    return null;
  }
}

/**
 * Clear the stored referral code (call after it has been consumed by signup).
 */
export function clearReferralCode(): void {
  try {
    sessionStorage.removeItem(REFERRAL_KEY);
    sessionStorage.removeItem(REFERRAL_TIME);
  } catch (_) { /* noop */ }
}
